import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLanguage } from "@/i18n/LanguageContext";
import { EASE } from "./shared";

const LangToggle = ({ testidPrefix }) => {
    const { lang, setLang } = useLanguage();
    return (
        <div className="flex items-center gap-1 text-xs uppercase tracking-[0.2em]">
            {["en", "nl"].map((l, i) => (
                <span key={l} className="flex items-center gap-1">
                    {i > 0 && <span className="text-ash/50">/</span>}
                    <button
                        data-testid={`${testidPrefix}-${l}`}
                        onClick={() => setLang(l)}
                        className={`transition-colors ${
                            lang === l ? "text-gold" : "text-ash hover:text-bone"
                        }`}
                    >
                        {l}
                    </button>
                </span>
            ))}
        </div>
    );
};

export const Navigation = ({ started }) => {
    const [open, setOpen] = useState(false);
    const { t } = useLanguage();
    const links = [
        { label: t.nav.manifesto, href: "#manifesto", testid: "nav-link-manifesto" },
        { label: t.nav.services, href: "#services", testid: "nav-link-services" },
        { label: t.nav.process, href: "#process", testid: "nav-link-process" },
        { label: "Consultancy", href: "/consultancy", testid: "nav-link-consultancy", external: true },
        { label: t.nav.contact, href: "#contact", testid: "nav-link-contact" },
    ];
    const go = (href) => {
        setOpen(false);
        scrollToId(href);
    };

    return (
        <>
            <motion.header
                data-testid="main-nav"
                initial={{ y: -80, opacity: 0 }}
                animate={started ? { y: 0, opacity: 1 } : {}}
                transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
                className="fixed inset-x-0 top-0 z-[80] border-b border-white/10 bg-black/60 backdrop-blur-xl"
            >
                <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-12">
                    <button
                        data-testid="nav-logo"
                        onClick={() => go("#top")}
                        className="transition-opacity hover:opacity-80"
                    >
                        <img
                            src="/logo/cbs-lockup-dark.svg"
                            alt="Clarke Business Solutions"
                            className="w-24 md:w-28"
                        />
                    </button>
                    <nav className="hidden items-center gap-10 md:flex">
                        {links.map((l) =>
                            l.external ? (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={l.testid}
                                    className="text-xs uppercase tracking-[0.25em] text-ash transition-colors hover:text-gold"
                                >
                                    {l.label}
                                </a>
                            ) : (
                                <button
                                    key={l.href}
                                    data-testid={l.testid}
                                    onClick={() => go(l.href)}
                                    className="text-xs uppercase tracking-[0.25em] text-ash transition-colors hover:text-gold"
                                >
                                    {l.label}
                                </button>
                            )
                        )}
                    </nav>
                    <div className="hidden items-center gap-6 md:flex">
                        <LangToggle testidPrefix="lang-toggle" />
                        <button
                            data-testid="nav-cta-button"
                            onClick={() => go("#contact")}
                            className="group flex items-center gap-2 border border-gold/50 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black"
                        >
                            {t.nav.cta}
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </button>
                    </div>
                    <button
                        data-testid="nav-menu-toggle"
                        className="text-bone md:hidden"
                        onClick={() => setOpen(!open)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>
            </motion.header>
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-10 bg-ink/95 backdrop-blur-xl md:hidden"
                    >
                        {links.map((l) =>
                            l.external ? (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={`mobile-${l.testid}`}
                                    className="font-display text-4xl text-bone transition-colors hover:text-gold"
                                >
                                    {l.label}
                                </a>
                            ) : (
                                <button
                                    key={l.href}
                                    data-testid={`mobile-${l.testid}`}
                                    onClick={() => go(l.href)}
                                    className="font-display text-4xl text-bone transition-colors hover:text-gold"
                                >
                                    {l.label}
                                </button>
                            )
                        )}
                        <LangToggle testidPrefix="mobile-lang-toggle" />
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
